import { useState, useCallback } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  CheckCircle2,
  Loader2,
  X,
} from "lucide-react";
import {
  useAnalyzeFile,
  useCreateMappingProfile,
} from "@/hooks/useMappingProfiles";
import type {
  ColumnMappingInput,
  TargetColumn,
  TargetSheet,
  WorkbookMetadata,
  AIMappingResult,
} from "@/types/mappingProfile";
import {
  TransformConfigEditor,
  transformNeedsConfig,
} from "@/components/mapping/TransformConfigEditor";
import { SearchableSelect } from "@/components/mapping/SearchableSelect";
import {
  TRANSFORM_OPTION_GROUPS,
  transformHint,
} from "@/components/mapping/transformOptions";
import { api } from "@/utils/axiosInterceptor";

const TYPE_CODE_OPTIONS = [
  { value: "01", label: "01 - Invoice" },
  { value: "01A", label: "01A - Consolidated" },
  { value: "02", label: "02 - Credit Note" },
  { value: "03", label: "03 - Debit Note" },
  { value: "04", label: "04 - Refund Note" },
  { value: "11", label: "11 - Self-Billed Invoice" },
  { value: "12", label: "12 - Self-Billed Credit Note" },
  { value: "13", label: "13 - Self-Billed Debit Note" },
  { value: "14", label: "14 - Self-Billed Refund Note" },
];

// Default invoice type codes per template.
const TEMPLATE_DEFAULT_TYPE_CODES: Record<string, string[]> = {
  invoice: ["01", "01A"],
  invoice_note: ["02", "03", "04"],
  self_billed_invoice: ["11"],
  self_billed_invoice_note: ["12", "13", "14"],
};

const TEMPLATE_LABELS: Record<string, string> = {
  invoice: "Invoice",
  invoice_note: "CN/DN/RN",
  self_billed_invoice: "Self-Billed Invoice",
  self_billed_invoice_note: "Self-Billed CN/DN/RN",
};

interface TargetTemplateInfo {
  template: {
    templateType: string;
    sheets: TargetSheet[];
    groupingKeyColumn: string;
  };
  columnsFlat: { sheet: string; column: TargetColumn }[];
}

// Radix Select disallows empty string value
const NONE_VALUE = "__none__";

// Abbreviation expansion
const TOKEN_SYNONYMS: Record<string, string> = {
  nbr: "number",
  no: "number",
  num: "number",
  reg: "registration",
  regn: "registration",
  registrationno: "registration",
  addr: "address",
  pp: "passport",
  tel: "contact",
  phone: "contact",
  mobile: "contact",
  hp: "contact",
  email: "email",
  qty: "quantity",
  amt: "amount",
  desc: "description",
  uom: "measurement",
  unit: "measurement",
  ref: "reference",
  cust: "buyer",
  customer: "buyer",
};

const NOISE_TOKENS = new Set(["the", "of", "for", "and"]);

const tokenize = (name: string): string[] =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((t) => TOKEN_SYNONYMS[t] ?? t)
    .filter((t) => t && !NOISE_TOKENS.has(t));

const normalizeKey = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, "");

interface FlatSource {
  sheet: string;
  column: string;
  tokens: string[];
  key: string;
}

/**
 * Deterministically match a target column
 */
const findSourceMatch = (
  targetTokens: string[],
  targetKey: string,
  sources: FlatSource[],
  foreignTokens: Set<string>,
): FlatSource | null => {
  // 1. Exact normalized match.
  const exact = sources.find((s) => s.key === targetKey);
  if (exact) return exact;

  // 2. Token-subset match with foreign-token rejection.
  const tset = new Set(targetTokens);
  let best: FlatSource | null = null;
  let bestExtra = Infinity;
  for (const s of sources) {
    const sset = new Set(s.tokens);
    const hasAll = targetTokens.every((t) => sset.has(t));
    if (!hasAll) continue;
    const extras = s.tokens.filter((t) => !tset.has(t));
    if (extras.some((t) => foreignTokens.has(t))) continue;
    if (extras.length < bestExtra) {
      best = s;
      bestExtra = extras.length;
    }
  }
  return best;
};

// Suggested transform for a target column
const defaultTransformFor = (col: {
  type: string;
  enumType?: string;
}): string => {
  if (col.type === "date") return "date_reformat";
  if (col.enumType) return `enum_${col.enumType}`;
  return "none";
};

const CreateMappingProfilePage = () => {
  const [, navigate] = useLocation();
  const [step, setStep] = useState(1);

  // Step 1: Profile info
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [templateType, setTemplateType] = useState("invoice");
  const [typeCodes, setTypeCodes] = useState<string[]>(["01"]);
  const [headerRow, setHeaderRow] = useState(1);
  const [dataStartRow, setDataStartRow] = useState(2);
  const [strictMode, setStrictMode] = useState(true);

  // Step 2: File upload
  const [file, setFile] = useState<File | null>(null);

  // Step 3: Analysis result
  const [metadata, setMetadata] = useState<WorkbookMetadata | null>(null);
  const [targetTemplate, setTargetTemplate] =
    useState<TargetTemplateInfo | null>(null);

  // Step 4: Column mappings
  const [columns, setColumns] = useState<ColumnMappingInput[]>([]);
  const [groupingKeySourceSheet, setGroupingKeySourceSheet] = useState("");
  const [groupingKeySourceColumn, setGroupingKeySourceColumn] = useState("");
  const [aiResult, setAiResult] = useState<AIMappingResult | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  const { analyzeFile, isAnalyzing } = useAnalyzeFile();
  const { createProfile, isCreating } = useCreateMappingProfile();

  // ─── Step navigation ─────────────────────────────────────────────────────

  const canProceedStep1 = name.trim() && templateType && typeCodes.length > 0;
  const canProceedStep2 = !!file;

  const isColumnRequired = (col: ColumnMappingInput) =>
    strictMode && !!col.isRequired;
  const isColumnMapped = (col: ColumnMappingInput) =>
    !!(col.sourceSheet && col.sourceColumn);

  const missingRequiredColumns = columns.filter(
    (c) => isColumnRequired(c) && !isColumnMapped(c),
  );
  const groupingKeyMissing = !(
    groupingKeySourceSheet && groupingKeySourceColumn
  );
  const canProceedStep4 =
    columns.length > 0 &&
    missingRequiredColumns.length === 0 &&
    !groupingKeyMissing;

  const handleAnalyze = useCallback(async () => {
    if (!file) return;

    try {
      // The analyzer reads headers from `headerRow` (its dataStartRow param == header row).
      const result = await analyzeFile({
        file,
        dataStartRow: headerRow,
        templateType,
      });
      setMetadata(result.metadata);

      // Fetch target template info
      const templateRes = await api.get<TargetTemplateInfo>(
        `/mapping-profiles/templates/${templateType}`,
      );
      setTargetTemplate(templateRes.data);

      // Check if we got AI suggestions
      const ai =
        result.aiSuggestions && "suggestions" in result.aiSuggestions
          ? (result.aiSuggestions as AIMappingResult)
          : null;
      setAiResult(ai);

      if (result.aiSuggestions && "error" in result.aiSuggestions) {
        setAiError((result.aiSuggestions as { error: string }).error);
      } else if (!result.aiSuggestions) {
        setAiError(
          "AI mapping not available — API key may not be configured on the server.",
        );
      } else {
        setAiError(null);
      }

      // Build initial column mappings, pre-filling from AI if available
      if (templateRes.data?.columnsFlat) {
        const groupingCol = templateRes.data.template.groupingKeyColumn;

        // Flatten all source columns
        const flatSources: FlatSource[] = [];
        for (const sheet of result.metadata.sheets) {
          const seen = new Set<string>();
          for (const c of sheet.columns) {
            if (!c.name.trim() || seen.has(c.name)) continue;
            seen.add(c.name);
            flatSources.push({
              sheet: sheet.name,
              column: c.name,
              tokens: tokenize(c.name),
              key: normalizeKey(c.name),
            });
          }
        }

        // Union of all target tokens
        const allTargetTokens = new Set<string>();
        for (const tc of templateRes.data.columnsFlat) {
          for (const t of tokenize(tc.column.name)) allTargetTokens.add(t);
        }

        // Exclude grouping key from per-sheet mappings
        const initialMappings: ColumnMappingInput[] =
          templateRes.data.columnsFlat
            .filter((tc) => tc.column.name !== groupingCol)
            .map((tc) => {
              const aiMatch = ai?.suggestions.find(
                (s) =>
                  s.targetSheet === tc.sheet &&
                  s.targetColumn === tc.column.name,
              );

              const fallbackTransform = defaultTransformFor(tc.column);

              if (aiMatch && aiMatch.sourceSheet && aiMatch.sourceColumn) {
                const aiTransform =
                  aiMatch.transformType && aiMatch.transformType !== "none"
                    ? aiMatch.transformType
                    : fallbackTransform;
                return {
                  sourceSheet: aiMatch.sourceSheet,
                  sourceColumn: aiMatch.sourceColumn,
                  targetSheet: tc.sheet,
                  targetColumn: tc.column.name,
                  transformType: aiTransform,
                  transformConfig: aiMatch.transformConfig ?? undefined,
                  isRequired: tc.column.required,
                  aiConfidence: aiMatch.confidence,
                  aiReasoning: aiMatch.reasoning,
                };
              }

              // Deterministic header-name fallback
              const targetTokens = tokenize(tc.column.name);
              const foreign = new Set(
                [...allTargetTokens].filter((t) => !targetTokens.includes(t)),
              );
              const match = findSourceMatch(
                targetTokens,
                normalizeKey(tc.column.name),
                flatSources,
                foreign,
              );

              return {
                sourceSheet: match?.sheet ?? "",
                sourceColumn: match?.column ?? "",
                targetSheet: tc.sheet,
                targetColumn: tc.column.name,
                transformType: fallbackTransform,
                isRequired: tc.column.required,
                aiReasoning: match ? "Auto-matched by column name" : undefined,
              };
            });
        setColumns(initialMappings);

        // Auto-fill grouping key from AI
        if (ai?.suggestions) {
          const groupingKey = ai.suggestions.find(
            (s) =>
              s.targetColumn === templateRes.data.template.groupingKeyColumn,
          );
          if (groupingKey) {
            setGroupingKeySourceSheet(groupingKey.sourceSheet);
            setGroupingKeySourceColumn(groupingKey.sourceColumn);
          }
        }
      }

      setStep(4);
    } catch {
      // Error handled by hook toast
    }
  }, [file, headerRow, templateType, analyzeFile]);

  const handleCreate = useCallback(async () => {
    try {
      const validColumns = columns.filter(
        (c) => c.sourceSheet && c.sourceColumn,
      );

      // Inject grouping key mappings for every target sheet
      const groupingCol = targetTemplate?.template.groupingKeyColumn;
      const allColumns = [...validColumns];

      if (groupingCol && groupingKeySourceSheet && groupingKeySourceColumn) {
        const targetSheets =
          targetTemplate?.template.sheets.map((s) => s.name) ?? [];
        for (const sheetName of targetSheets) {
          allColumns.push({
            sourceSheet: groupingKeySourceSheet,
            sourceColumn: groupingKeySourceColumn,
            targetSheet: sheetName,
            targetColumn: groupingCol,
            transformType: "none",
            isRequired: true,
          });
        }
      }

      await createProfile({
        name,
        description: description || undefined,
        vendorName: vendorName || undefined,
        templateType,
        applicableTypeCodes: typeCodes,
        defaultStrictMode: strictMode,
        dataStartRow,
        headerRow,
        sourceSheetCount: metadata?.totalSheets,
        sourceFingerprint: metadata?.fingerprint,
        sourceColumns: metadata?.sheets.map((s) => ({
          sheet: s.name,
          columns: s.columns.map((c) => c.name),
        })),
        groupingKeySourceSheet: groupingKeySourceSheet || undefined,
        groupingKeySourceColumn: groupingKeySourceColumn || undefined,
        aiGenerated: !!aiResult,
        aiModel: aiResult?.model,
        overallConfidence: aiResult?.overallConfidence,
        columns: allColumns,
      });

      navigate("/mapping-profiles");
    } catch {
      // Error handled by hook toast
    }
  }, [
    columns,
    name,
    description,
    vendorName,
    templateType,
    typeCodes,
    strictMode,
    dataStartRow,
    headerRow,
    metadata,
    groupingKeySourceSheet,
    groupingKeySourceColumn,
    targetTemplate,
    aiResult,
    createProfile,
    navigate,
  ]);

  // ─── Column mapping helpers ──────────────────────────────────────────────

  const updateColumnMapping = (
    targetSheet: string,
    targetColumn: string,
    field: keyof ColumnMappingInput,
    value: string,
  ) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.targetSheet === targetSheet && col.targetColumn === targetColumn
          ? { ...col, [field]: value }
          : col,
      ),
    );
  };

  const updateColumnConfig = (
    targetSheet: string,
    targetColumn: string,
    transformConfig: Record<string, unknown>,
  ) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.targetSheet === targetSheet && col.targetColumn === targetColumn
          ? { ...col, transformConfig }
          : col,
      ),
    );
  };

  // Selecting a sheet resets the column
  const handleSourceSheetChange = (
    targetSheet: string,
    targetColumn: string,
    val: string,
  ) => {
    const sheet = val === NONE_VALUE ? "" : val;
    setColumns((prev) =>
      prev.map((col) =>
        col.targetSheet === targetSheet && col.targetColumn === targetColumn
          ? { ...col, sourceSheet: sheet, sourceColumn: "" }
          : col,
      ),
    );
  };

  const handleSourceColumnChange = (
    targetSheet: string,
    targetColumn: string,
    val: string,
  ) => {
    updateColumnMapping(
      targetSheet,
      targetColumn,
      "sourceColumn",
      val === NONE_VALUE ? "" : val,
    );
  };

  const sourceSheetOptions = [
    ...new Set(metadata?.sheets.map((s) => s.name) ?? []),
  ];

  const getSourceColumnsForSheet = (sheetName: string): string[] => {
    const sheet = metadata?.sheets.find((s) => s.name === sheetName);
    return [...new Set(sheet?.columns.map((c) => c.name) ?? [])];
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/mapping-profiles")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Create Mapping Profile</h1>
          <p className="text-muted-foreground">Step {step} of 5</p>
        </div>
      </div>

      {/* Step 1: Profile Info */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Profile Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Altek POS - Invoice"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="vendor">Vendor Name</Label>
              <Input
                id="vendor"
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                placeholder="e.g., Altek POS"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="desc">Description</Label>
              <Textarea
                id="desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this mapping profile"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Target Template Type *</Label>
              <Select
                value={templateType}
                onValueChange={(val) => {
                  setTemplateType(val);
                  setTypeCodes(TEMPLATE_DEFAULT_TYPE_CODES[val] ?? []);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice">Invoice Template</SelectItem>
                  <SelectItem value="invoice_note">
                    CN/DN/RN Template
                  </SelectItem>
                  <SelectItem value="self_billed_invoice">
                    Self-Billed Invoice Template
                  </SelectItem>
                  <SelectItem value="self_billed_invoice_note">
                    Self-Billed CN/DN/RN Template
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Applicable Invoice Type Codes *</Label>
              <p className="text-xs text-muted-foreground">
                Only type codes valid for the selected template are shown. To
                map a different document type (e.g. a Credit Note), change the
                Target Template Type above first.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {TYPE_CODE_OPTIONS.filter((opt) =>
                  (TEMPLATE_DEFAULT_TYPE_CODES[templateType] ?? []).includes(
                    opt.value,
                  ),
                ).map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={typeCodes.includes(opt.value)}
                      onCheckedChange={(checked) => {
                        setTypeCodes((prev) =>
                          checked
                            ? [...prev, opt.value]
                            : prev.filter((v) => v !== opt.value),
                        );
                      }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-6">
              <div className="space-y-2">
                <Label htmlFor="headerRow">Header Row</Label>
                <Input
                  id="headerRow"
                  type="number"
                  min={1}
                  max={100}
                  value={headerRow}
                  onChange={(e) => {
                    const v = Number(e.target.value) || 1;
                    setHeaderRow(v);
                    // Keep data start after the header row.
                    if (dataStartRow <= v) setDataStartRow(v + 1);
                  }}
                  className="w-24"
                />
                <p className="text-xs text-muted-foreground">
                  Row containing the column headers (usually 1).
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="startRow">Data Start Row</Label>
                <Input
                  id="startRow"
                  type="number"
                  min={2}
                  max={100}
                  value={dataStartRow}
                  onChange={(e) =>
                    setDataStartRow(
                      Math.max(
                        headerRow + 1,
                        Number(e.target.value) || headerRow + 1,
                      ),
                    )
                  }
                  className="w-24"
                />
                <p className="text-xs text-muted-foreground">
                  First row of real data. Set higher than the header row to skip
                  a style/spacer row (e.g. Altek = 3).
                </p>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!canProceedStep1}>
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Upload File */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Upload Sample File</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a sample export file from your POS or accounting system.
              This file is used ONLY to read column headers and sample values.
              It will NOT be stored or imported.
            </p>

            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="font-medium">{file.name}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setFile(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm font-medium">
                    Drop .xlsx here or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground">Max 5 MB</p>
                  <input
                    type="file"
                    className="hidden"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setFile(f);
                    }}
                  />
                </label>
              )}
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                onClick={handleAnalyze}
                disabled={!canProceedStep2 || isAnalyzing}
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    Analyze <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 is shown briefly during analysis — we skip directly to step 4 */}

      {/* Step 4: Column Mapping */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>Map Columns</CardTitle>
            {metadata && (
              <p className="text-sm text-muted-foreground">
                Source file: {metadata.totalSheets} sheet(s),{" "}
                {metadata.totalColumns} columns detected. Map source columns to
                QuickBill target fields.
              </p>
            )}
            {aiResult && (
              <div className="flex items-center gap-2 text-sm mt-2 p-2 rounded-md bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800">
                <Badge
                  variant="secondary"
                  className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                >
                  AI
                </Badge>
                <span>
                  {aiResult.suggestions.length} columns auto-mapped (confidence:{" "}
                  {Math.round(aiResult.overallConfidence * 100)}%). Review and
                  adjust as needed.
                </span>
              </div>
            )}
            {aiError && !aiResult && (
              <div className="flex items-center gap-2 text-sm mt-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800">
                <Badge
                  variant="secondary"
                  className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                >
                  Manual
                </Badge>
                <span>
                  AI mapping unavailable: {aiError}. Map columns manually below.
                </span>
              </div>
            )}

            {/* Validation mode*/}
            <div className="mt-3 space-y-2">
              <Label className="text-xs">Validation Mode</Label>
              <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 p-1 gap-1">
                <button
                  type="button"
                  onClick={() => setStrictMode(true)}
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition-all ${
                    strictMode
                      ? "bg-white dark:bg-slate-900 shadow-sm"
                      : "text-slate-500 dark:text-slate-400"
                  }`}
                >
                  Strict
                </button>
                <button
                  type="button"
                  onClick={() => setStrictMode(false)}
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition-all ${
                    !strictMode
                      ? "bg-white dark:bg-slate-900 shadow-sm"
                      : "text-slate-500 dark:text-slate-400"
                  }`}
                >
                  Permissive
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                {strictMode
                  ? "Strict: fields marked with * must be mapped — QuickBill will not substitute defaults."
                  : "Permissive: only the grouping key is required; QuickBill fills missing fields with defaults."}
              </p>
            </div>

            {(missingRequiredColumns.length > 0 || groupingKeyMissing) && (
              <div className="mt-2 text-sm p-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
                <p className="font-semibold">
                  {missingRequiredColumns.length + (groupingKeyMissing ? 1 : 0)}{" "}
                  required item(s) still need attention before you can continue:
                </p>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  {groupingKeyMissing && (
                    <li>
                      Grouping Key (POS Reference Number source) is not set
                    </li>
                  )}
                  {missingRequiredColumns.slice(0, 8).map((c) => (
                    <li key={`${c.targetSheet}-${c.targetColumn}`}>
                      {c.targetSheet} / {c.targetColumn}
                    </li>
                  ))}
                  {missingRequiredColumns.length > 8 && (
                    <li>…and {missingRequiredColumns.length - 8} more</li>
                  )}
                </ul>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Grouping Key */}
            <div className="p-4 border rounded-lg bg-muted/50 space-y-3">
              <Label className="font-semibold">
                Grouping Key (POS Reference Number source)
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Source Sheet</Label>
                  <SearchableSelect
                    value={groupingKeySourceSheet}
                    options={sourceSheetOptions}
                    placeholder="Select sheet"
                    allowClear={false}
                    className="h-10 text-sm"
                    onChange={(v) => {
                      setGroupingKeySourceSheet(v);
                      setGroupingKeySourceColumn("");
                    }}
                  />
                </div>
                <div>
                  <Label className="text-xs">Source Column</Label>
                  <SearchableSelect
                    value={groupingKeySourceColumn}
                    options={getSourceColumnsForSheet(groupingKeySourceSheet)}
                    placeholder="Select column"
                    allowClear={false}
                    className="h-10 text-sm"
                    onChange={setGroupingKeySourceColumn}
                  />
                </div>
              </div>
            </div>

            {/* Column mappings by target sheet */}
            {targetTemplate?.template.sheets.map((sheet) => {
              const sheetMappings = columns.filter(
                (c) => c.targetSheet === sheet.name,
              );
              if (sheetMappings.length === 0) return null;

              return (
                <div key={sheet.name} className="space-y-3">
                  <h3 className="font-semibold text-sm border-b pb-1">
                    {sheet.name}
                  </h3>
                  <div className="space-y-2">
                    {sheetMappings.map((mapping) => {
                      const targetCol = sheet.columns.find(
                        (c) => c.name === mapping.targetColumn,
                      );
                      const required = strictMode && !!targetCol?.required;
                      const missingRequired =
                        required &&
                        !(mapping.sourceSheet && mapping.sourceColumn);

                      return (
                        <div
                          key={`${mapping.targetSheet}-${mapping.targetColumn}`}
                          className={`grid grid-cols-12 gap-2 items-center text-sm rounded px-1 ${
                            missingRequired
                              ? "bg-red-50 dark:bg-red-950/30 ring-1 ring-red-300 dark:ring-red-800"
                              : ""
                          }`}
                        >
                          {/* Target */}
                          <div className="col-span-3 flex items-center gap-1">
                            {required && (
                              <span className="text-destructive">*</span>
                            )}
                            <span
                              className={`truncate ${missingRequired ? "text-red-600 dark:text-red-400 font-medium" : ""}`}
                            >
                              {mapping.targetColumn}
                            </span>
                          </div>

                          {/* Arrow */}
                          <div className="col-span-1 text-center text-muted-foreground">
                            &larr;
                          </div>

                          {/* Source sheet */}
                          <div className="col-span-3">
                            <SearchableSelect
                              value={mapping.sourceSheet}
                              options={sourceSheetOptions}
                              placeholder="Sheet"
                              onChange={(val) =>
                                handleSourceSheetChange(
                                  mapping.targetSheet,
                                  mapping.targetColumn,
                                  val,
                                )
                              }
                            />
                          </div>

                          {/* Source column */}
                          <div className="col-span-3">
                            <SearchableSelect
                              value={mapping.sourceColumn}
                              options={getSourceColumnsForSheet(
                                mapping.sourceSheet,
                              )}
                              placeholder="Column"
                              onChange={(val) =>
                                handleSourceColumnChange(
                                  mapping.targetSheet,
                                  mapping.targetColumn,
                                  val,
                                )
                              }
                            />
                          </div>

                          {/* Transform indicator */}
                          <div className="col-span-2">
                            <Select
                              value={mapping.transformType || "none"}
                              onValueChange={(val) =>
                                updateColumnMapping(
                                  mapping.targetSheet,
                                  mapping.targetColumn,
                                  "transformType",
                                  val,
                                )
                              }
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {TRANSFORM_OPTION_GROUPS.map((group) => (
                                  <SelectGroup key={group.label}>
                                    <SelectLabel className="text-xs">
                                      {group.label}
                                    </SelectLabel>
                                    {group.options.map((opt) => (
                                      <SelectItem
                                        value={opt.value}
                                        key={opt.value}
                                      >
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {transformHint(mapping.transformType) && (
                            <p className="col-span-12 pl-8 text-xs text-muted-foreground">
                              {transformHint(mapping.transformType)}
                            </p>
                          )}
                          {mapping.sourceSheet &&
                            mapping.sourceColumn &&
                            transformNeedsConfig(
                              mapping.transformType || "none",
                            ) && (
                              <div className="col-span-12 pl-8 pb-1">
                                <TransformConfigEditor
                                  transformType={
                                    mapping.transformType || "none"
                                  }
                                  config={mapping.transformConfig ?? null}
                                  sourceColumns={getSourceColumnsForSheet(
                                    mapping.sourceSheet,
                                  )}
                                  onChange={(c) =>
                                    updateColumnConfig(
                                      mapping.targetSheet,
                                      mapping.targetColumn,
                                      c,
                                    )
                                  }
                                />
                              </div>
                            )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button onClick={() => setStep(5)} disabled={!canProceedStep4}>
                Review <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Review & Save */}
      {step === 5 && (
        <Card>
          <CardHeader>
            <CardTitle>Review & Save</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Name:</span>{" "}
                <span className="font-medium">{name}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Vendor:</span>{" "}
                <span className="font-medium">{vendorName || "-"}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Template:</span>{" "}
                <span className="font-medium">
                  {TEMPLATE_LABELS[templateType] ?? templateType}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Type Codes:</span>{" "}
                {typeCodes.map((tc) => (
                  <Badge key={tc} variant="outline" className="mr-1">
                    {tc}
                  </Badge>
                ))}
              </div>
              <div>
                <span className="text-muted-foreground">Grouping Key:</span>{" "}
                <span className="font-medium">
                  {groupingKeySourceSheet
                    ? `${groupingKeySourceSheet}.${groupingKeySourceColumn}`
                    : "Not set"}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Mapped Columns:</span>{" "}
                <span className="font-medium">
                  {
                    columns.filter((c) => c.sourceSheet && c.sourceColumn)
                      .length
                  }{" "}
                  / {columns.length}
                </span>
              </div>
            </div>

            {/* Summary of mapped columns */}
            <div className="border rounded-lg p-3 max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1">Target</th>
                    <th className="text-left py-1">Source</th>
                    <th className="text-left py-1">Transform</th>
                  </tr>
                </thead>
                <tbody>
                  {columns
                    .filter((c) => c.sourceSheet && c.sourceColumn)
                    .map((c, i) => (
                      <tr key={i} className="border-b">
                        <td className="py-1">
                          {c.targetSheet}.{c.targetColumn}
                        </td>
                        <td className="py-1">
                          {c.sourceSheet}.{c.sourceColumn}
                        </td>
                        <td className="py-1">
                          {c.transformType !== "none" ? c.transformType : "-"}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(4)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button onClick={handleCreate} disabled={isCreating}>
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Profile"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CreateMappingProfilePage;
